/**
 * Parse an HTTP `retry-after` header (RFC 9110 §10.2.3): delta-seconds or
 * HTTP-date → milliseconds, floored at the 1s minimum (a 0s or past value still
 * waits 1s). Large values are returned UN-capped: the retry policy treats
 * anything beyond its ceiling as terminal (fail now) rather than a silent
 * multi-minute hang (spec §5). Malformed or absent values return undefined
 * (caller falls back to exponential backoff).
 */
const MIN_MS = 1_000;

export const parseRetryAfter = (headerValue: string | null | undefined): number | undefined => {
  if (!headerValue) return undefined;
  const raw = headerValue.trim();

  if (/^\d+$/.test(raw)) {
    return Math.max(Number(raw) * 1000, MIN_MS);
  }

  // Date.parse is implementation-lenient (e.g. V8 parses '-3' as a valid
  // timestamp), so only attempt it on strings that look like an HTTP-date
  // (day/month names, 'GMT', etc.); delta-seconds are handled above.
  const dateMs = /[a-zA-Z]/.test(raw) ? Date.parse(raw) : NaN;
  if (!Number.isNaN(dateMs)) {
    return Math.max(dateMs - Date.now(), MIN_MS);
  }

  return undefined;
};
